/**
 * ConnectCenter — SLIK Connect, the ONE messaging inbox (two-pane modal). (Jose 2026-10-30 redesign)
 *
 * Left: conversation list with CATEGORY FILTER PILLS (All · 🔴 Needs reply · 💬 Outreach · 🧾 Transactional
 *       · ⚙️ Operational) + a per-thread SOURCE chip (Waitlist / Receipt / Reminder / Message from X / Client
 *       reply). A "＋ New message" composer starts a thread from a name + phone.
 * Right: a context header (name · phone · Call) over the one reusable ClientMessageThread (compose + templates).
 * Mobile shows one pane at a time. Opened from the top-bar message icon.
 *
 * Source/category taxonomy lives in src/shared/connectSources.ts — the single source of truth the service
 * stamps and the inbox groups by.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X, MessageSquare, ChevronLeft, Search, Phone, Plus } from 'lucide-react';
import { communicationService, ConnectThread } from '../../services/communicationService';
import { ConnectCategory, CATEGORY_META, sourceMeta } from '../../shared/connectSources';
import ClientMessageThread from './ClientMessageThread';

interface Props {
  onClose: () => void;
  initialThread?: ConnectThread | null; // Layer-2 popup "Open" → land straight on this thread
  /** Staff member currently operating the app → tags their typed messages "Message from <name>". */
  operatorName?: string;
}

const initials = (name: string) =>
  name.split(' ').map((n) => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2) || '?';

/** Compact relative time for the conversation list: now / 5m / 3h / Mon / Mar 4. */
const relTime = (d: Date): string => {
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 6 * 86400) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

type Filter = 'all' | ConnectCategory;

/** Which filter pill a thread falls under — a client reply / unread always lands in Needs reply. */
const threadCategory = (t: ConnectThread): ConnectCategory =>
  (t.unread > 0 || t.lastDirection === 'in') ? 'needs_reply' : sourceMeta(t.lastSource, t.lastDirection).category;

const PILLS: Array<{ key: Filter; label: string; icon?: string }> = [
  { key: 'all', label: 'All' },
  { key: 'needs_reply', label: CATEGORY_META.needs_reply.label, icon: CATEGORY_META.needs_reply.icon },
  { key: 'outreach', label: CATEGORY_META.outreach.label, icon: CATEGORY_META.outreach.icon },
  { key: 'transactional', label: CATEGORY_META.transactional.label, icon: CATEGORY_META.transactional.icon },
  { key: 'operational', label: CATEGORY_META.operational.label, icon: CATEGORY_META.operational.icon },
];

const ConnectCenter: React.FC<Props> = ({ onClose, initialThread = null, operatorName }) => {
  const [threads, setThreads] = useState<ConnectThread[]>([]);
  const [active, setActive] = useState<ConnectThread | null>(initialThread);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [loaded, setLoaded] = useState(false);
  // "＋ New message" composer — start a thread from a name + phone.
  const [composing, setComposing] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');

  useEffect(() => {
    const unsub = communicationService.subscribeToThreads((t) => { setThreads(t); setLoaded(true); });
    return () => unsub();
  }, []);
  // Opened from the Layer-2 popup → jump straight to that thread (once, on mount).
  useEffect(() => { if (initialThread) setActive(initialThread); /* eslint-disable-next-line */ }, []);

  // Per-pill counts (unread threads for Needs reply, else thread count in that category).
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: threads.length, needs_reply: 0, outreach: 0, transactional: 0, operational: 0 };
    threads.forEach((t) => { c[threadCategory(t)] += 1; });
    return c;
  }, [threads]);

  const filtered = threads.filter((t) => {
    if (filter !== 'all' && threadCategory(t) !== filter) return false;
    if (!q) return true;
    return t.clientName.toLowerCase().includes(q.toLowerCase()) || t.phoneDigits.includes(q.replace(/\D/g, ''));
  });

  const startNew = () => {
    const digits = newPhone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return;
    setActive({
      phoneDigits: digits, clientId: '', clientName: newName.trim() || newPhone,
      phone: newPhone, lastMessage: '', lastAt: new Date(), unread: 0,
    });
    setComposing(false); setNewName(''); setNewPhone('');
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-stretch sm:items-center sm:justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 w-full sm:max-w-4xl sm:h-[85vh] h-full sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            {(active || composing) && (
              <button onClick={() => { setActive(null); setComposing(false); }} className="sm:hidden p-1 -ml-1 text-gray-500" title="Back">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <MessageSquare className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-gray-900 dark:text-white">
              {active ? active.clientName : composing ? 'New message' : 'SLIK Connect'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Thread list */}
          <div className={`${active || composing ? 'hidden sm:flex' : 'flex'} flex-col w-full sm:w-80 sm:border-r border-gray-200 dark:border-gray-700 min-h-0`}>
            <div className="p-2 pb-1 shrink-0 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search"
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <button
                onClick={() => { setActive(null); setComposing(true); }}
                className="shrink-0 w-9 h-9 rounded-lg bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center touch-manipulation"
                title="New message"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {/* CATEGORY FILTER PILLS */}
            <div className="flex gap-1.5 px-2 pb-2 pt-1 overflow-x-auto shrink-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
              {PILLS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setFilter(p.key)}
                  className={`shrink-0 text-xs px-2.5 py-1 rounded-full whitespace-nowrap touch-manipulation border transition-colors ${
                    filter === p.key
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {p.icon ? `${p.icon} ` : ''}{p.label}
                  {counts[p.key] > 0 && <span className={`ml-1 ${filter === p.key ? 'text-purple-100' : 'text-gray-400'}`}>{counts[p.key]}</span>}
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {!loaded ? (
                <div className="p-3 space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 animate-pulse">
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-800" />
                        <div className="h-2.5 w-3/4 rounded bg-gray-100 dark:bg-gray-800/60" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center px-6 mt-12 text-gray-400">
                  <MessageSquare className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm">{q || filter !== 'all' ? 'No matches.' : 'No conversations yet.'}</p>
                  {!q && filter === 'all' && <p className="text-xs mt-1">Texts with clients will show up here.</p>}
                </div>
              ) : (
                filtered.map((t) => {
                  const meta = sourceMeta(t.lastSource, t.lastDirection);
                  return (
                    <button
                      key={t.phoneDigits}
                      onClick={() => { setComposing(false); setActive(t); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                        active?.phoneDigits === t.phoneDigits ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center text-sm font-semibold shrink-0">
                        {initials(t.clientName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm text-gray-900 dark:text-white truncate ${t.unread > 0 ? 'font-bold' : 'font-medium'}`}>{t.clientName}</span>
                          <span className={`text-[10px] shrink-0 ${t.unread > 0 ? 'text-purple-600 font-semibold' : 'text-gray-400'}`}>
                            {relTime(t.lastAt)}
                          </span>
                        </div>
                        {/* SOURCE CHIP — where the newest message came from */}
                        <div className="flex items-center gap-1.5">
                          <span className={`shrink-0 px-1.5 py-[1px] rounded-full text-[9px] font-medium ${meta.tone}`}>{meta.label}</span>
                          <span className={`text-xs truncate ${t.unread > 0 ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>{t.lastMessage}</span>
                          {t.unread > 0 && (
                            <span className="shrink-0 min-w-[18px] h-[18px] px-1 ml-auto rounded-full bg-purple-600 text-white text-[10px] flex items-center justify-center font-bold">
                              {t.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Active thread / new-message composer */}
          <div className={`${active || composing ? 'flex' : 'hidden sm:flex'} flex-col flex-1 min-w-0 min-h-0`}>
            {composing ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3">
                <div className="w-full max-w-xs space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Start a new message</h3>
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Client name (optional)"
                    className="w-full px-3 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <input
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') startNew(); }}
                    inputMode="tel"
                    placeholder="Phone number"
                    className="w-full px-3 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    onClick={startNew}
                    disabled={newPhone.replace(/\D/g, '').length < 10}
                    className="w-full py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-40 touch-manipulation"
                  >
                    Start conversation
                  </button>
                  <p className="text-[11px] text-gray-400 text-center">Opens a thread. Your message is tagged “{operatorName ? `Message from ${operatorName.split(' ')[0]}` : 'Staff message'}”.</p>
                </div>
              </div>
            ) : active ? (
              <>
                {/* CONTEXT HEADER — who you're talking to + quick actions */}
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-900">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{active.clientName}</div>
                    {active.phone && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{active.phone}</div>}
                  </div>
                  {active.phone && (
                    <a
                      href={`tel:${active.phone.replace(/[^\d+]/g, '')}`}
                      className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 touch-manipulation"
                      title="Call"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  )}
                </div>
                <ClientMessageThread
                  clientId={active.clientId}
                  clientName={active.clientName}
                  phone={active.phone}
                  source="manual"
                  sentByName={operatorName}
                  className="w-full flex-1 min-h-0"
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                Select a conversation
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectCenter;
